import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetConfigurationQuery,
  useUpdateConfigurationMutation,
  useGetTenantSettingsQuery,
  type NotificationConfig,
} from '@/redux/internal-services/onboardingApi';
import { StepShell } from './StepShell';
import type { StepHandle, SubmitResult } from './stepHandle';

interface Props {
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  disabled?: boolean;
}

/**
 * Guided-setup "Quiet Hours" step — enable + time window during which non-critical
 * notifications are suppressed. Stored on the generic `notification` config (tenant layer)
 * alongside the delivery matrix; the timezone is the tenant's own timezone (read-only here).
 */
const QuietHoursStep = forwardRef<StepHandle, Props>(({ onBack, onNext, onSkip, disabled = false }, ref) => {
  const { t } = useTranslation();
  const { data: configResp } = useGetConfigurationQuery('notification');
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  const [updateConfiguration, { isLoading: saving }] = useUpdateConfigurationMutation();

  const cfg = configResp?.data?.effective_value as NotificationConfig | undefined;
  const tenantTimezone = (tenantSettings?.data as Record<string, unknown> | undefined)?.timezone as string | undefined;

  const [enabled, setEnabled] = useState(false);
  const [from, setFrom] = useState('22:00');
  const [to, setTo] = useState('07:00');
  const [error, setError] = useState('');

  // Synchronous mirror so submit/getState read the freshest values.
  const qhRef = useRef({ enabled, from, to });
  useEffect(() => { qhRef.current = { enabled, from, to }; }, [enabled, from, to]);

  useEffect(() => {
    if (!cfg?.quiet_hours) return;
    setEnabled(!!cfg.quiet_hours.enabled);
    if (cfg.quiet_hours.from) setFrom(cfg.quiet_hours.from);
    if (cfg.quiet_hours.to) setTo(cfg.quiet_hours.to);
  }, [cfg]);

  // Voice actuator — set the quiet-hours window. Omitted from/to keep the current value.
  const setQuietHours = (on: boolean, f?: string, t?: string) => {
    const next = { enabled: on, from: f ?? qhRef.current.from, to: t ?? qhRef.current.to };
    qhRef.current = next;
    setEnabled(next.enabled);
    setFrom(next.from);
    setTo(next.to);
  };

  const persist = async (): Promise<SubmitResult> => {
    setError('');
    try {
      await updateConfiguration({
        key: 'notification',
        value: { quiet_hours: { ...qhRef.current } },
      }).unwrap();
      return { ok: true };
    } catch {
      setError(t('common.failedToSave'));
      return { ok: false, reason: 'transient', message: 'Failed to save quiet hours.' };
    }
  };

  const handleNext = () => { void persist().then((r) => { if (r.ok) onNext(); }); };

  useImperativeHandle(ref, (): StepHandle => ({
    getState: () => ({ ...qhRef.current, tenantTimezone }),
    submit: persist,
    setQuietHours,
  }));

  const timeCls =
    'border border-ui-border rounded-lg px-3 py-2.5 text-sm bg-ui-input focus:outline-none focus:ring-2 focus:ring-brand-dark/40 focus:border-brand-dark';

  return (
    <StepShell
      title="Quiet Hours"
      subtitle="Personalise your quiet hours settings"
      onBack={onBack}
      onNext={handleNext}
      onSkip={onSkip}
      disabled={disabled}
      saving={saving}
      error={error}
    >
      <div className="flex items-center justify-between mb-5">
        <span className="text-sm font-medium text-ui-text">Enable Quiet Hours</span>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => setEnabled((v) => !v)}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
            enabled ? 'bg-brand-dark border border-brand-dark' : 'bg-gray-200 border border-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
              enabled ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      <label className="block text-sm font-medium text-ui-text mb-1.5">Time range</label>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <span className="block text-xs text-ui-text3 mb-1">From</span>
          <input type="time" value={from} disabled={!enabled} onChange={(e) => setFrom(e.target.value)} className={`w-full ${timeCls} disabled:opacity-50`} />
        </div>
        <div className="flex-1">
          <span className="block text-xs text-ui-text3 mb-1">To</span>
          <input type="time" value={to} disabled={!enabled} onChange={(e) => setTo(e.target.value)} className={`w-full ${timeCls} disabled:opacity-50`} />
        </div>
      </div>

      <div className="text-sm text-ui-text2">
        Tenant Timezone: <span className="font-medium text-ui-text">{tenantTimezone?.replace(/_/g, ' ') || '—'}</span>
      </div>
      <p className="text-xs text-ui-text3 mt-3">
        If enabled, Critical notifications will still be delivered through enabled channels during quiet hours.
      </p>
    </StepShell>
  );
});

QuietHoursStep.displayName = 'QuietHoursStep';

export default QuietHoursStep;
